import React from "react";

import Picture from "../general/Picture";
import PlatformLinks from "../general/PlatformLinks";
import { prefersReducedMotion, hasFinePointer } from "../hooks/motion";

const SLIDES = [
  {
    name: "hero-1",
    alt: "Jo-Ann Strauss in a black trench coat, standing beside a red bus in a London square",
    className: "hero-slide-img hero-slide-img-1",
  },
  {
    name: "hero-2",
    alt: "Jo-Ann Strauss walking through a gallery at an art fair, holding a catalogue",
    className: "hero-slide-img hero-slide-img-2",
  },
];

const SLIDE_SECONDS = 6; //Time each photo is shown, including the 1.2s slide
const TILT_DEGREES = 18; //Max tilt either way when the mouse is at the edge of the title

const ArrowIcon = () => (
  <svg
    viewBox={"0 0 200 200"}
    aria-hidden={"true"}
    focusable={"false"}
  >
    <path d={"m131.5 51.5-8.1 8.1L158.1 95H20v11h138.1l-34.7 34.4 8.1 8.1L180 100l-48.5-48.5z"} />
  </svg>
);

//Full width slideshow with the ARTdacity title over it, as on the original site
const Hero = () => {
  //The new photo slides in while the previous one slides out ("direction" is 1 for next, -1 for back)
  const [{ index, previous, direction }, setSlide] = React.useState({ index: 0, previous: null, direction: 1 });
  const isAutoPlaying = React.useMemo(() => !prefersReducedMotion(), []);
  const [isPageVisible, setIsPageVisible] = React.useState(true);
  const titleRef = React.useRef(null);

  //Move to the next photo every few seconds (paused while the tab is hidden). Using the arrows restarts the timer
  React.useEffect(() => {
    if (!isAutoPlaying || !isPageVisible) {
      return undefined;
    }
    const timer = window.setTimeout(() => showSlide(1), SLIDE_SECONDS * 1000);
    return () => window.clearTimeout(timer);
  }, [index, isAutoPlaying, isPageVisible]);

  React.useEffect(() => {
    const onVisibilityChange = () => setIsPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const showSlide = (step) => {
    setSlide((s) => ({ index: (s.index + step + SLIDES.length) % SLIDES.length, previous: s.index, direction: step }));
  };

  //The title tilts in 3D towards the mouse (the original site's "3D tilt" effect)
  const canTilt = React.useMemo(() => hasFinePointer() && !prefersReducedMotion(), []);

  const onTitlePointerMove = (e) => {
    const el = titleRef.current;
    if (!canTilt || !el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty("--tilt-x", `${(-y * 2 * TILT_DEGREES).toFixed(2)}deg`);
    el.style.setProperty("--tilt-y", `${(x * 2 * TILT_DEGREES).toFixed(2)}deg`);
  };

  const onTitlePointerLeave = () => {
    const el = titleRef.current;
    if (el) {
      el.style.setProperty("--tilt-x", "0deg");
      el.style.setProperty("--tilt-y", "0deg");
    }
  };

  return (
    <section
      className={"hero"}
      aria-roledescription={"carousel"}
      aria-label={"ARTdacity"}
    >
      <div className={`hero-slides ${previous === null ? "is-first" : ""} ${direction < 0 ? "is-backwards" : ""}`}>
        {SLIDES.map((slide, i) => (
          <div
            key={slide.name}
            className={`hero-slide ${i === index ? "is-active" : ""} ${i === previous ? "is-previous" : ""}`}
            aria-roledescription={"slide"}
            aria-label={`${i + 1} of ${SLIDES.length}`}
            aria-hidden={i !== index}
          >
            <Picture
              name={slide.name}
              widths={[900, 1600, 2400]}
              fallbackWidth={1200}
              sizes={"100vw"}
              alt={slide.alt}
              width={"1200"}
              height={"800"}
              className={slide.className}
              eager={i === 0}
            />
          </div>
        ))}
      </div>

      <h1
        ref={titleRef}
        className={"hero-title"}
        onPointerMove={onTitlePointerMove}
        onPointerLeave={onTitlePointerLeave}
      >
        <span className={"hero-title-name"}>
          <span className={"hero-title-art"}>ART</span>
          <span className={"hero-title-dacity"}>dacity</span>
        </span>
        {" "}
        <span className={"hero-title-by"}>by Jo-Ann Strauss</span>
      </h1>

      <PlatformLinks className={"hero-platforms"} />

      <button
        type={"button"}
        className={"hero-arrow hero-arrow-prev"}
        onClick={() => showSlide(-1)}
        aria-label={"Previous photo"}
      >
        <ArrowIcon />
      </button>
      <button
        type={"button"}
        className={"hero-arrow hero-arrow-next"}
        onClick={() => showSlide(1)}
        aria-label={"Next photo"}
      >
        <ArrowIcon />
      </button>
    </section>
  );
};

export default Hero;
